import type {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Store } from '@ngrx/store';
import { type Observable, from, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';
import { selectCurrentAdmin } from '../../+state/platform-identity/platform-identity.selectors';
import type { RootState } from '../interfaces/root-state.interface';

/**
 * Attaches the platform-admin's Firebase token to outgoing requests and retries
 * once with a fresh token on 401. Platform admins have NO customer context, so
 * none of spog-client's customer/claims headers are sent.
 */
export const platformAuthInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const store = inject(Store<RootState>);
  const auth = inject(Auth);

  return store.select(selectCurrentAdmin).pipe(
    take(1),
    switchMap((admin) => {
      const authedReq = addTokenToRequest(req, admin?.token);

      return next(authedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status !== 401) {
            return throwError(() => error);
          }
          const user = auth.currentUser;
          if (!user) {
            return throwError(
              () => new Error('User not authenticated for token refresh.')
            );
          }
          return from(user.getIdToken(true)).pipe(
            switchMap((freshToken) =>
              next(addTokenToRequest(req, freshToken))
            )
          );
        })
      );
    })
  );
};

function addTokenToRequest(
  req: HttpRequest<unknown>,
  token: string | undefined | null
): HttpRequest<unknown> {
  const headers: { 'x-cdw-app-name': string; Authorization?: string } = {
    'x-cdw-app-name': 'cdw-platform-admin',
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return req.clone({ setHeaders: headers });
}
